/**
 * WizardGenerate Component
 * Step 4: Generate quiz questions with multi-chunk support
 * Now includes QuickSettings for generation configuration
 */
import React, { useState, useCallback, useRef } from 'react';
import QuickSettings from '../QuickSettings';

function ChunkProgress({ chunks, currentChunkIndex, results }) {
  return (
    <div className="space-y-2">
      {chunks.map((chunk, index) => {
        const result = results[index];
        const isCurrent = index === currentChunkIndex;
        const isCompleted = result?.success !== undefined;
        const isSuccess = result?.success === true;
        const isFailed = result?.success === false;
        const isPending = !isCompleted && !isCurrent;
        
        return (
          <div
            key={index}
            className={`
              p-3 rounded-lg border transition-all
              ${isCurrent 
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                : isSuccess
                  ? 'border-success-500 bg-success-50 dark:bg-success-900/20'
                  : isFailed
                    ? 'border-error-500 bg-error-50 dark:bg-error-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }
            `}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                  ${isCurrent
                    ? 'bg-primary-500 text-white'
                    : isSuccess
                      ? 'bg-success-500 text-white'
                      : isFailed
                        ? 'bg-error-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }
                `}>
                  {isSuccess ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : isFailed ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : index + 1}
                </span>
                <div>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {chunk.label}
                  </span>
                  {result?.questions && (
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                      ({result.questions.length} Fragen)
                    </span>
                  )}
                </div>
              </div>
              
              {isCurrent && (
                <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm">Verarbeite...</span>
                </div>
              )}
              
              {isFailed && (
                <span className="text-sm text-error-600 dark:text-error-400">
                  {result.error}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QuestionPreview({ questions }) {
  const [expanded, setExpanded] = useState(null);
  
  if (!questions || questions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Noch keine Fragen generiert
      </div>
    );
  }
  
  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {questions.map((q, index) => (
        <div
          key={index}
          className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            onClick={() => setExpanded(expanded === index ? null : index)}
            className="w-full px-4 py-3 flex items-center justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 theme-transition text-left"
          >
            <span className="text-gray-800 dark:text-gray-200">
              <span className="font-mono text-xs bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded mr-2">
                {q.type || 'mc'}
              </span>
              {q.question?.substring(0, 80)}
              {q.question?.length > 80 && '...'}
            </span>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${expanded === index ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expanded === index && (
            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
              <pre className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono overflow-x-auto">
                {JSON.stringify(q, null, 2)}
              </pre>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function WizardGenerate({
  chunks,
  currentChunkIndex,
  results,
  isGenerating,
  onStartGeneration,
  onStopGeneration,
  onExport,
  onPrev,
  totalQuestions,
  // QuickSettings props
  numberOfQuestions,
  setNumberOfQuestions,
  difficulty,
  setDifficulty,
  includeCodeBlocks,
  setIncludeCodeBlocks,
  multipleChoiceMode,
  setMultipleChoiceMode,
  userGuidance,
  setUserGuidance,
  topicTags,
  currentTag,
  setCurrentTag,
  onAddTopicTag,
  onRemoveTopicTag,
  // PDF processing mode props
  pdfProcessingMode,
  setPdfProcessingMode,
  imageQuality,
  setImageQuality,
  hasPdfFiles,
  supportsVision,
  // LLM Provider for native upload availability
  llmProvider
}) {
  const [showResults, setShowResults] = useState(false);
  
  // Aggregate all questions
  const allQuestions = results
    .filter(r => r?.success && r?.questions)
    .flatMap(r => r.questions);
  
  // Calculate progress
  const completedChunks = results.filter(r => r?.success !== undefined).length;
  const successfulChunks = results.filter(r => r?.success === true).length;
  const failedChunks = results.filter(r => r?.success === false).length;
  const progressPercent = chunks.length > 0 
    ? Math.round((completedChunks / chunks.length) * 100) 
    : 0;
  
  // Check if native API upload is supported
  const supportsNativeUpload = ['openai', 'anthropic', 'openrouter'].includes(llmProvider);
  
  // Processing mode options for PDFs
  const processingModes = [
    { id: 'local', label: 'Lokal parsen', available: true },
    { id: 'native', label: 'API senden', available: supportsNativeUpload },
    { id: 'images', label: 'Als Bilder', available: supportsVision }
  ];
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          Fragen generieren
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {chunks.length} Chunk{chunks.length !== 1 && 's'} werden verarbeitet
        </p>
      </div>
      
      {/* PDF Processing Mode - Show at top when PDFs are present */}
      {hasPdfFiles && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            PDF-Verarbeitung:
          </div>
          
          {/* Triple Switch */}
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
            {processingModes.map((mode) => {
              const isSelected = pdfProcessingMode === mode.id;
              const isDisabled = !mode.available;
              
              return (
                <button
                  key={mode.id}
                  onClick={() => mode.available && setPdfProcessingMode(mode.id)}
                  disabled={isDisabled || isGenerating}
                  className={`
                    flex-1 px-3 py-2 text-sm font-medium transition-all
                    ${isSelected 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                    ${isDisabled || isGenerating ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                    ${mode.id !== 'local' ? 'border-l border-gray-300 dark:border-gray-600' : ''}
                  `}
                >
                  {mode.label}
                </button>
              );
            })}
          </div>
          
          {/* Image Quality Selector - Only show for images mode */}
          {pdfProcessingMode === 'images' && (
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs text-gray-600 dark:text-gray-400">Bildqualität:</span>
              <div className="flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
                {['low', 'medium', 'high'].map((quality) => (
                  <button
                    key={quality}
                    onClick={() => setImageQuality(quality)}
                    disabled={isGenerating}
                    className={`
                      px-3 py-1 text-xs font-medium theme-transition
                      ${imageQuality === quality 
                        ? 'bg-secondary-600 text-white' 
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }
                      ${isGenerating ? 'opacity-40 cursor-not-allowed' : ''}
                      ${quality !== 'low' ? 'border-l border-gray-300 dark:border-gray-600' : ''}
                    `}
                  >
                    {quality === 'low' ? 'Niedrig' : quality === 'medium' ? 'Mittel' : 'Hoch'}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Mode Description */}
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {pdfProcessingMode === 'local' && 'Text wird lokal extrahiert. Günstig, funktioniert überall.'}
            {pdfProcessingMode === 'native' && 'PDF wird an die API gesendet. Bessere Formatierung, höhere Kosten.'}
            {pdfProcessingMode === 'images' && 'PDF-Seiten als Bilder für Vision-Modelle. Ideal für Diagramme/Grafiken.'}
          </div>
        </div>
      )}
      
      {/* Generation Settings - QuickSettings Panel */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <QuickSettings
          numberOfQuestions={numberOfQuestions}
          setNumberOfQuestions={setNumberOfQuestions}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
          includeCodeBlocks={includeCodeBlocks}
          setIncludeCodeBlocks={setIncludeCodeBlocks}
          multipleChoiceMode={multipleChoiceMode}
          setMultipleChoiceMode={setMultipleChoiceMode}
          userGuidance={userGuidance}
          setUserGuidance={setUserGuidance}
          topicTags={topicTags}
          currentTag={currentTag}
          setCurrentTag={setCurrentTag}
          onAddTopicTag={onAddTopicTag}
          onRemoveTopicTag={onRemoveTopicTag}
        />
      </div>
      
      {/* Progress Section */}
      {(isGenerating || completedChunks > 0) && (
        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Fortschritt: {completedChunks} / {chunks.length} Chunks
              </span>
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {progressPercent}%
              </span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  failedChunks > 0 
                    ? 'bg-gradient-to-r from-success-500 to-error-500'
                    : 'bg-success-500'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          
          {/* Chunk Details */}
          <ChunkProgress
            chunks={chunks}
            currentChunkIndex={isGenerating ? currentChunkIndex : -1}
            results={results}
          />
          
          {/* Summary */}
          {completedChunks === chunks.length && (
            <div className={`
              p-4 rounded-lg border
              ${failedChunks > 0
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
                : 'bg-success-50 dark:bg-success-900/20 border-success-300 dark:border-success-700'
              }
            `}>
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {failedChunks > 0 ? (
                    <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    Generierung abgeschlossen
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {allQuestions.length} Fragen aus {successfulChunks} Chunks erstellt
                    {failedChunks > 0 && ` (${failedChunks} Fehler)`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Results Toggle */}
      {allQuestions.length > 0 && (
        <div className="space-y-3">
          <button
            onClick={() => setShowResults(!showResults)}
            className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline"
          >
            <svg
              className={`w-5 h-5 transition-transform ${showResults ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showResults ? 'Ergebnisse ausblenden' : `Ergebnisse anzeigen (${allQuestions.length} Fragen)`}
          </button>
          
          {showResults && <QuestionPreview questions={allQuestions} />}
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        {!isGenerating && completedChunks < chunks.length && (
          <button
            onClick={onStartGeneration}
            className="px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 theme-transition bg-primary-500 hover:bg-primary-600 text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {completedChunks > 0 ? 'Fortsetzen' : `${numberOfQuestions} Fragen generieren`}
          </button>
        )}
        
        {isGenerating && (
          <button
            onClick={onStopGeneration}
            className="px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 bg-error-500 hover:bg-error-600 text-white theme-transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            Stoppen
          </button>
        )}
        
        {allQuestions.length > 0 && !isGenerating && (
          <button
            onClick={onExport}
            className="px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 bg-success-500 hover:bg-success-600 text-white theme-transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Exportieren ({allQuestions.length} Fragen)
          </button>
        )}
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onPrev}
          disabled={isGenerating}
          className={`
            px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 theme-transition
            ${isGenerating
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }
          `}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurück
        </button>
        
        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
          {allQuestions.length > 0 && (
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {allQuestions.length} Fragen generiert
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default WizardGenerate;
